<script lang="ts">
import { SelectItemType } from '@/app/common/types/selectItem.type';
import { ComplaintInfo } from '@/interface/Complaint.interface';
import { ResponsiblePerson } from '@/interface/ResponsiblePerson.interface';
import { userService } from '@/services/serviceProvider';
import { ref } from 'vue';

export default {
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        header: {
            type: String,
            default: 't-admin-complaint-caretaker-edit'
        },
        cancelText: {
            type: String,
            default: 't-cancel'
        },
        confirmText: {
            type: String,
            default: 't-confirm'
        },
        complaint: {
            type: Object,
            default: {}
        }
    },
    emits: [
        'update:modelValue',
        'update:complaint',
        'onConfirm',
        'onCancel'
    ],
    setup() {
        const editResposibleForm = ref();
        return { editResposibleForm };
    },
    data() {
        return {
            request: this.complaint as ComplaintInfo,
            newResponsibleId: undefined as string | undefined,
            rules: {
                required: (message: any) => {
                    return (v: any) => !!v || message;
                },
            }
        }
    },
    computed: {
        dialogShow: {
            get() {
                let activePerson = undefined as ResponsiblePerson | undefined;
                if (this.isMainDpm) {
                    activePerson = this.request.relatedMainDepartment?.responiblePersons?.find(o => o.activeResponsible)
                } else if (this.isSubDpm) {
                    activePerson = this.request.relatedSubDepartments
                        ?.find(o => o.departmentCode === this.currentDpm)
                        ?.responiblePersons?.find(o => o.activeResponsible)
                }
                this.newResponsibleId = activePerson?.employeeId;
                return this.modelValue || false
            },
            set(newValue: boolean) {
                this.$emit('update:modelValue', newValue);
            }
        },
        currentDpm() {
            return userService.currentDepartment();
        },
        isMainDpm(): boolean {
            return this.request.relatedMainDepartment?.departmentCode === this.currentDpm;
        },
        isSubDpm(): boolean {
            const dpmCodes = this.request.relatedSubDepartments?.map(o => o.departmentCode);
            const currentDpm = this.currentDpm;
            if (dpmCodes && currentDpm) return dpmCodes.includes(currentDpm);
            return false;
        },
        responsiblesInDpm(): SelectItemType[] {
            if (this.isMainDpm) {
                return this.request.relatedMainDepartment?.responiblePersons?.map(o => (
                    {
                        label: `${o.title} ${o.firstName} ${o.lastName}`,
                        value: o.employeeId
                    } as SelectItemType
                )) || [];
            } else if (this.isSubDpm) {
                return this.request.relatedSubDepartments?.find(o => o.departmentCode === this.currentDpm)?.responiblePersons?.map(o => (
                    {
                        label: `${o.title} ${o.firstName} ${o.lastName}`,
                        value: o.employeeId
                    } as SelectItemType
                )) || [];
            }
            return [];
        },
        responsibleEmpId(): string | undefined {
            let responsibleEmpId = undefined as string | undefined;
            let complaint = this.complaint as ComplaintInfo;
            if (this.isMainDpm)
                responsibleEmpId = complaint.relatedMainDepartment?.responiblePersons?.find(o => o.activeResponsible)?.employeeId;
            else if (this.isSubDpm)
                responsibleEmpId = complaint.relatedSubDepartments?.find(s => s.departmentCode === this.currentDpm)?.responiblePersons?.find(o => o.activeResponsible)?.employeeId;
            return responsibleEmpId;
        },
        disabled() {
            return this.responsibleEmpId === this.newResponsibleId;
        }
    },
    methods: {
        async confirm() {
            const valid = await this.editResposibleForm.validate();
            if (valid.valid) {
                this.dialogShow = false;
                this.$emit('onConfirm', this.newResponsibleId);
            }
        },
        cancel() {
            this.dialogShow = false;
            this.$emit('onCancel')
        },
    }
}
</script>

<template>
    <v-dialog v-model="dialogShow" width="60%" :close-on-back="false" persistent>
        <v-form ref="editResposibleForm" class="admin-form app-form-input" validate-on="submit lazy"
            @submit.prevent="confirm">
            <v-card class="dialog-card">
                <v-toolbar density="compact">
                    <v-toolbar-title>{{ $t(header) }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-toolbar-items>
                        <v-btn icon="mdi-close" @click="dialogShow = false"></v-btn>
                    </v-toolbar-items>
                </v-toolbar>
                <v-divider></v-divider>
                <v-card-text>
                    <div class="text-label">{{ $t('t-admin-complaint-caretaker') }}</div>
                    <v-select v-model="newResponsibleId" class="hide-float-label" variant="outlined"
                        :label="$t('t-please-select')" hide-details="auto" :items="responsiblesInDpm" item-value="value"
                        item-title="label" density="comfortable"
                        :rules="[rules.required(`${$t('t-please-select')}${$t('t-admin-complaint-caretaker')}`)]">
                    </v-select>
                </v-card-text>
                <v-card-actions>
                    <v-container class="mx-0">
                        <div class="d-flex justify-end">
                            <v-btn @click="cancel" variant="outlined" color="primary" size="large">
                                {{ $t(cancelText) }}
                            </v-btn>
                            <v-btn @click="confirm" variant="flat" color="primary" size="large" :disabled="disabled">
                                {{ $t(confirmText) }}
                            </v-btn>
                        </div>
                    </v-container>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>